// categoryTree — pure helpers that turn the /api/categories tree + the
// /api/decks list into a single drill-down model for the Topics picker.
// Kept framework-free so it can be unit-tested without mounting Vue.
//
// Each deck carries a `category` slug path [domain, subject, topic] — file decks
// from decks/*.json, library/public decks resolved from categoryId by
// /api/decks (routes/public.js). Decks are slotted onto their category node by
// that path (deepest resolvable ancestor wins). Library (your own) decks are
// ALSO collected into a synthetic "My decks" group for quick flat access, so
// they appear in both places. A non-library deck with no resolvable path falls
// back to a synthetic "Unsorted" node.

const MY_DECKS_COLOR = "#2563eb";
const PUBLIC_COLOR = "#7c3aed";
const UNSORTED_COLOR = "#64748b";

// buildCategoryNodes — clone the category tree into picker nodes, inheriting the
// domain colour downward and indexing every node by its full slug path. Returns
// { roots, pathIndex }. Pure; does not mutate the input.
function buildCategoryNodes(categories) {
  const pathIndex = new Map();
  const clone = (node, parentColor, parentPath) => {
    const color = node.color || parentColor || null;
    const path = [...parentPath, node.slug];
    const out = {
      id: node.id,
      level: node.level,
      name: node.name,
      slug: node.slug,
      color,
      scope: node.scope || (node.tenantId == null ? "global" : "tenant"),
      decks: [],
      children: (node.children || []).map((c) => clone(c, color, path))
    };
    pathIndex.set(path.join("/"), out);
    return out;
  };
  const roots = (categories || []).map((d) => clone(d, null, []));
  return { roots, pathIndex };
}

// countDecks — annotate each node with deckCount = own decks + all descendants.
function countDecks(node) {
  let n = node.decks.length;
  for (const child of node.children) n += countDecks(child);
  node.deckCount = n;
  return n;
}

// buildPickerModel — the public entry. Returns { roots } where roots is the
// ordered list of top-level cards to render: the category domains, then (if any)
// a "My decks" node and an "Unsorted" node. Every node has { name, slug, color,
// level, decks[], children[], deckCount }, so the picker renders them uniformly.
export function buildPickerModel(categories, decks) {
  const list = Array.isArray(decks) ? decks : [];
  const { roots, pathIndex } = buildCategoryNodes(categories);

  const myDecks = [];
  const publicDecks = [];
  for (const deck of list) {
    // Published decks (from any tenant) sit in their own "Public decks" group —
    // their categories belong to the publisher's taxonomy, not the viewer's tree.
    if (deck.source === "public") { publicDecks.push(deck); continue; }
    // Your own (library) decks ALSO appear flat under "My decks" for quick
    // access — but they are STILL slotted onto their Domain→Subject→Topic node
    // below, so they surface under the category headings as content scales.
    const isLibrary = deck.source === "library";
    if (isLibrary) myDecks.push(deck);
    const path = Array.isArray(deck.category) ? deck.category : null;
    let target = null;
    if (path) {
      // Prefer the exact node; fall back to the deepest resolvable ancestor.
      for (let i = path.length; i >= 1; i--) {
        const node = pathIndex.get(path.slice(0, i).join("/"));
        if (node) { target = node; break; }
      }
    }
    if (target) target.decks.push(deck);
    // No resolvable category → "Unsorted", EXCEPT a library deck, which is
    // already reachable under "My decks".
    else if (!isLibrary) (pathIndex.get("__unsorted__") || stash(pathIndex)).decks.push(deck);
  }

  roots.forEach(countDecks);

  // Hide empty branches — a category with nothing playable under it shouldn't
  // clutter the picker (e.g. a domain whose only decks belong to a hidden
  // collection, or a freshly-created category with no ready deck yet).
  const prune = (nodes) => nodes
    .filter((n) => n.deckCount > 0)
    .map((n) => ({ ...n, children: prune(n.children) }));

  const out = prune(roots);
  if (myDecks.length) {
    out.push({
      name: "My decks", slug: "my-decks", color: MY_DECKS_COLOR, level: "library",
      synthetic: true, decks: myDecks, children: [], deckCount: myDecks.length
    });
  }
  if (publicDecks.length) {
    out.push({
      name: "Public decks", slug: "public-decks", color: PUBLIC_COLOR, level: "public",
      synthetic: true, decks: publicDecks, children: [], deckCount: publicDecks.length
    });
  }
  const unsorted = pathIndex.get("__unsorted__");
  if (unsorted && unsorted.decks.length) {
    out.push({
      name: "Unsorted", slug: "unsorted", color: UNSORTED_COLOR, level: "topic",
      synthetic: true, decks: unsorted.decks, children: [], deckCount: unsorted.decks.length
    });
  }
  return { roots: out };
}

// stash — lazily create the catch-all "Unsorted" bucket in the index.
function stash(pathIndex) {
  const node = { name: "Unsorted", slug: "unsorted", color: UNSORTED_COLOR, level: "topic", decks: [], children: [], deckCount: 0 };
  pathIndex.set("__unsorted__", node);
  return node;
}
